import { Box, Stack, Typography, useTheme } from '@mui/material';
import { MouseEvent, ReactElement, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Skeleton from '../../../component/skeleton/Skeleton';
import { placeTooltip } from '../../dashboard/periodChart';

export type ActivityBarSeries = {
  label: string;
  color: string;
};

export type ActivityBar = {
  /** Stable key, usually the period's start date. */
  key: string;
  /** Short x-axis label. */
  label: string;
  /** Heading of the tooltip. */
  title: string;
  /** One value per series, in the series' own order. */
  values: number[];
  /** Extra lines under the values in the tooltip. */
  notes?: { name: string; value: string }[];
};

type ActivityBarChartProps = {
  bars: ActivityBar[];
  series: ActivityBarSeries[];
  fetching: boolean;
  height?: number;
  /** Trailing average per series, drawn dashed. Same shape as values. */
  trend?: (number | undefined)[][];
  trendLabel?: string;
  formatValue?: (value: number) => string;
  /** Whole numbers on the axis, for counts rather than rates. */
  wholeNumbers?: boolean;
  emptyText?: string;
  /** Label every nth period. 1 labels them all; 3 thins a crowded axis. */
  labelEvery?: number;
  /** Grow to fill the height the card has left, using `height` as a floor.
   *  Used to land the axis labels on the same line as a neighbouring card. */
  fill?: boolean;
  /** Opens the period under the cursor. The whole column is the hit target,
   *  not the bar itself, so a quiet period is as easy to click as a busy one. */
  onSelect?: (index: number) => void;
};

// The marks. A bar caps at a fixed width and keeps the rest of its slot as air,
// rather than growing fatter as the card widens; the cap is rounded at the data
// end and square on the baseline, because a bar grows out of the axis.
const BAR_CAP = 14;
const BAR_RADIUS = 3;
const PAIR_GAP = 2;
const AXIS = { left: 38, bottom: 24, top: 10, right: 14 };

/** Round axis ticks: 0 / 50 / 100, never 0 / 47 / 94. */
const ticksFor = (max: number, wholeNumbers: boolean, count = 4): { top: number; values: number[] } => {
  if (!max || !Number.isFinite(max)) return { top: 1, values: [0, 1] };
  const raw = max / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  let step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? 10 * magnitude;
  if (wholeNumbers) step = Math.max(1, Math.round(step));
  const top = Math.ceil(max / step) * step;
  const values: number[] = [];
  for (let v = 0; v <= top + 1e-9; v += step) values.push(Number(v.toFixed(6)));
  return { top: top || 1, values };
};

/** A bar with its data end rounded and its baseline end square. */
const barPath = (x: number, y: number, width: number, height: number): string => {
  if (height <= 0) return '';
  const r = Math.max(0, Math.min(BAR_RADIUS, width / 2, height));
  return `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${
    y + height
  } Z`;
};

/**
 * Grouped bars: one cluster per period, one bar per series.
 *
 * Drawn in real pixels rather than a stretched unit box. A stretched drawing
 * makes bar width a percentage of the card, so bars fatten as the card widens,
 * corner radii come out as ovals and a hairline is a different weight on each
 * axis. Measuring the container and drawing at 1:1 is what lets the marks be
 * specified rather than approximated.
 *
 * Grouped rather than stacked on purpose: a stacked segment reads as a share of
 * one total, and these series are not parts of a whole. Equipment out and in
 * are two directions, and the two boomerang classes are two different problems
 * that must never be added together.
 */
export const ActivityBarChart = ({
  bars,
  series,
  fetching,
  height = 240,
  trend,
  trendLabel,
  formatValue = (value) => String(Math.round(value)),
  wholeNumbers = true,
  emptyText = 'Nothing moved in this period',
  labelEvery = 1,
  fill = false,
  onSelect,
}: ActivityBarChartProps): ReactElement => {
  const theme = useTheme();
  const [hover, setHover] = useState<{ bar: number; y: number } | undefined>();
  const plotRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: height });

  // The drawing needs a real size before it can lay anything out in pixels.
  useEffect(() => {
    const node = plotRef.current;
    if (!node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setBox({ w: rect.width, h: fill ? Math.max(height, rect.height) : height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetching, bars.length, fill, height]);

  const width = box.w;
  const boxHeight = box.h;

  const scale = useMemo(() => {
    const tallest = Math.max(0, ...bars.flatMap((bar) => bar.values), ...(trend || []).flat().map((v) => v ?? 0));
    return ticksFor(tallest, wholeNumbers);
  }, [bars, trend, wholeNumbers]);

  const inner = Math.max(0, width - AXIS.left - AXIS.right);
  const plotHeight = Math.max(0, boxHeight - AXIS.top - AXIS.bottom);
  const band = inner / Math.max(1, bars.length);
  // Bars share the middle of their slot; whatever is left over is air.
  const barWidth = Math.max(2, Math.min(BAR_CAP, (band - PAIR_GAP - 6) / Math.max(1, series.length)));
  const clusterWidth = barWidth * series.length + PAIR_GAP * (series.length - 1);
  const barX = (index: number, seriesIndex: number) => AXIS.left + band * index + (band - clusterWidth) / 2 + seriesIndex * (barWidth + PAIR_GAP);
  const centreX = (index: number) => AXIS.left + band * index + band / 2;
  const y = (value: number) => AXIS.top + plotHeight - (value / scale.top) * plotHeight;

  const tipRef = useRef<HTMLDivElement>(null);
  const [tipSize, setTipSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (tipRef.current) {
      const box = tipRef.current.getBoundingClientRect();
      if (box.width !== tipSize.w || box.height !== tipSize.h) setTipSize({ w: box.width, h: box.height });
    }
  }, [hover?.bar, tipSize.w, tipSize.h]);

  const barAt = useCallback(
    (clientX: number) => {
      const box = plotRef.current?.getBoundingClientRect();
      if (!box || !bars.length) return undefined;
      return Math.min(bars.length - 1, Math.max(0, Math.floor(((clientX - box.left) / box.width) * bars.length)));
    },
    [bars.length],
  );

  const onMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const box = plotRef.current?.getBoundingClientRect();
      const bar = barAt(event.clientX);
      if (!box || bar === undefined) return;
      setHover({ bar, y: event.clientY - box.top });
    },
    [barAt],
  );

  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const bar = barAt(event.clientX);
      if (bar !== undefined) onSelect?.(bar);
    },
    [barAt, onSelect],
  );

  const tipPosition = () => {
    const box = plotRef.current?.getBoundingClientRect();
    if (!hover || !box) return { left: 0, top: 0 };
    return placeTooltip(box, hover.bar, bars.length, hover.y, tipSize);
  };

  const surface = theme.palette.background.paper;
  const hovered = hover ? bars[hover.bar] : undefined;

  return (
    <Box sx={fill ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, columnGap: '14px', rowGap: 0.5 }}>
        {series.map((s) => (
          <Stack key={s.label} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
              {s.label}
            </Typography>
          </Stack>
        ))}
        {trend && trendLabel && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 14, height: 0, borderTop: `2px dashed ${theme.palette.text.secondary}` }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
              {trendLabel}
            </Typography>
          </Stack>
        )}
      </Stack>
      {fetching ? (
        <Skeleton fetching height={height} />
      ) : !bars.length ? (
        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      ) : (
        <Box
          ref={plotRef}
          sx={{
            position: 'relative',
            height: fill ? 'auto' : height,
            ...(fill && { flexGrow: 1, minHeight: height }),
            cursor: onSelect ? 'pointer' : 'default',
          }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(undefined)}
          onClick={onSelect ? onClick : undefined}
        >
          {width > 0 && (
            <svg width={width} height={boxHeight} viewBox={`0 0 ${width} ${boxHeight}`} style={{ display: 'block', overflow: 'visible' }}>
              {scale.values.map((tick) => (
                <g key={tick}>
                  <line
                    x1={AXIS.left}
                    x2={width - AXIS.right}
                    y1={y(tick)}
                    y2={y(tick)}
                    stroke={tick === 0 ? theme.palette.grey[400] : theme.palette.grey[200]}
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                  />
                  <text
                    x={AXIS.left - 8}
                    y={y(tick) + 3.5}
                    textAnchor="end"
                    fill={theme.palette.text.secondary}
                    fontSize={10.5}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {tick.toLocaleString()}
                  </text>
                </g>
              ))}
              {onSelect && hover && (
                <rect x={AXIS.left + band * hover.bar} y={AXIS.top} width={band} height={plotHeight} fill={theme.palette.action.hover} pointerEvents="none" />
              )}
              {bars.map((bar, index) =>
                series.map((s, si) =>
                  bar.values[si] ? (
                    <path
                      key={`${bar.key}-${s.label}`}
                      d={barPath(barX(index, si), y(bar.values[si]), barWidth, AXIS.top + plotHeight - y(bar.values[si]))}
                      fill={s.color}
                      fillOpacity={hover && hover.bar !== index ? 0.4 : 1}
                    />
                  ) : null,
                ),
              )}
              {trend &&
                series.map((s, si) => {
                  const points = bars
                    .map((_, index) => (trend[si]?.[index] === undefined ? '' : `${centreX(index)},${y(trend[si][index] as number)}`))
                    .filter(Boolean)
                    .join(' ');
                  if (!points) return null;
                  return (
                    <g key={`trend-${s.label}`} pointerEvents="none">
                      {/* a surface halo keeps the line legible where it crosses a bar */}
                      <polyline points={points} fill="none" stroke={surface} strokeWidth={5} strokeLinejoin="round" />
                      <polyline points={points} fill="none" stroke={s.color} strokeWidth={2} strokeDasharray="5 3" strokeLinejoin="round" />
                    </g>
                  );
                })}
              {bars.map((bar, index) =>
                index % labelEvery || !bar.label ? null : (
                  <text key={bar.key} x={centreX(index)} y={boxHeight - 7} textAnchor="middle" fill={theme.palette.text.secondary} fontSize={10.5}>
                    {bar.label}
                  </text>
                ),
              )}
            </svg>
          )}
          {hover && hovered && (
            <Box
              ref={tipRef}
              sx={{
                position: 'absolute',
                ...tipPosition(),
                pointerEvents: 'none',
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 1,
                boxShadow: 2,
                px: 1.25,
                py: 0.75,
                zIndex: 2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {hovered.title}
              </Typography>
              {series.map((s, si) => (
                <Stack key={s.label} direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 150 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {formatValue(hovered.values[si] || 0)}
                  </Typography>
                </Stack>
              ))}
              {hovered.notes?.map((note) => (
                <Stack key={note.name} direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, pl: '14px' }}>
                    {note.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {note.value}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export const LegendSwatch = ({ color, children }: { color: string; children: ReactNode }): ReactElement => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
      {children}
    </Typography>
  </Stack>
);

export default ActivityBarChart;
